import { useCallback, useState } from 'react';
import { ChevronDownIcon } from '@zola/zola-ui/src/components/SvgIconsV3/ChevronDown';
import { PlayerPlayIcon } from '@zola/zola-ui/src/components/SvgIconsV3/PlayerPlay';
import { RotateClockwiseIcon } from '@zola/zola-ui/src/components/SvgIconsV3/RotateClockwise';
import { XIcon } from '@zola/zola-ui/src/components/SvgIconsV3/X';
import useEffectOnce from '@zola/zola-ui/src/hooks/useEffectOnce';
import { COLORS3 } from '@zola/zola-ui/src/styles/emotion';
import {
  animation,
  button,
  frame,
  introVideo,
  large,
  overlay,
  playButton,
  restartButton,
  restartIcon,
  screenOverlay,
} from './IntroVideo.styles';

declare global {
  interface Window {
    /**
     * Reference to the YouTube IFrame API
     *
     * @see https://developers.google.com/youtube/iframe_api_reference
     */
    YT: any; // eslint-disable-line @typescript-eslint/no-explicit-any

    /**
     * Reference to the YouTube player object
     */
    player: any; // eslint-disable-line @typescript-eslint/no-explicit-any

    /**
     * Called by the YouTube IFrame API after the script is loaded
     */
    onYouTubeIframeAPIReady: () => void;
  }
}

const loadYouTubeIframeAPI = () => {
  const scriptElement = document.createElement('script');
  scriptElement.src = 'https://www.youtube.com/iframe_api';
  const bodyElement = document.getElementsByTagName('body')[0];
  bodyElement.appendChild(scriptElement);

  window.onYouTubeIframeAPIReady = () => {
    window.player = new window.YT.Player('player');
  };
};

const getYouTubeEmbedUrl = (videoId: string) =>
  `https://www.youtube.com/embed/${videoId}?playlist=${videoId}&color=white&autoplay=1&mute=1&loop=1&enablejsapi=1`;

interface IntroVideoProps {
  /**
   * The YouTube video ID
   */
  videoId: string;
}

export const IntroVideo = ({ videoId }: IntroVideoProps) => {
  const [paused, setPaused] = useState(false);
  const [closed, setClosed] = useState(false);
  const [enlarged, setEnlarged] = useState(false);

  useEffectOnce(() => {
    loadYouTubeIframeAPI();
  });

  const close = useCallback(() => {
    setClosed(true);
  }, []);

  const minimize = useCallback(() => {
    setEnlarged(false);
    window.player?.mute();
  }, []);

  const restart = useCallback(() => {
    window.player?.seekTo(0);
  }, []);

  const onOverlayClick = useCallback(() => {
    if (!enlarged) {
      setEnlarged(true);
      window.player?.unMute();
    } else if (paused) {
      setPaused(false);
      window.player?.playVideo();
    } else if (!paused) {
      setPaused(true);
      window.player?.pauseVideo();
    }
  }, [enlarged, paused]);

  if (closed) {
    return null;
  }

  return (
    <>
      {enlarged && (
        <div css={screenOverlay} onClick={minimize} aria-hidden style={{ maxWidth: 'unset' }} />
      )}
      <div css={[introVideo, animation, enlarged && large]}>
        <iframe
          css={frame}
          id="player"
          width="100%"
          height="100%"
          src={getYouTubeEmbedUrl(videoId)}
          title="Zola Registry"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
        <div css={overlay} onClick={onOverlayClick} aria-hidden>
          {paused && (
            <div css={playButton}>
              <PlayerPlayIcon width={24} height={24} color={COLORS3.WHITE_100} />
            </div>
          )}
        </div>
        {!enlarged && (
          <button css={button} onClick={close} aria-label="Close video">
            <XIcon width={12} height={12} color={COLORS3.WHITE_100} title="Close video" />
          </button>
        )}
        {enlarged && (
          <button css={button} onClick={minimize} aria-label="Minimize video">
            <ChevronDownIcon
              width={12}
              height={12}
              color={COLORS3.WHITE_100}
              title="Minimize video"
            />
          </button>
        )}
        {enlarged && !paused && (
          <button css={restartButton} onClick={restart} aria-label="Restart video">
            <RotateClockwiseIcon
              css={restartIcon}
              width={10}
              height={10}
              color={COLORS3.WHITE_100}
              title="Restart video"
            />
          </button>
        )}
      </div>
    </>
  );
};
